import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { MatProgressBar } from '@angular/material/progress-bar';
import moment from 'moment';
import { Moment } from 'moment';
import { ButtonPadraoComponent } from '../../shared/components/button-padrao/button-padrao.component';
import { SelectOneComponent } from '../../shared/components/select-one/select-one.component';
import { DatePickerPadraoComponent } from '../../shared/components/date-picker-padrao/date-picker-padrao.component';
import { FormFieldComponent } from '../../shared/components/form-field/form-field.component';
import { MsgService } from '../../shared/service/msg.service';
import { WebhookService } from '../../shared/service/webhook.service';
import { UsuarioService } from '../../shared/service/usuario.service';
import { WebhookModel } from '../../shared/model/webhook-model';
import { UsuarioResumo } from '../../shared/model/usuario/usuario-resumo-model';

@Component({
  selector: 'app-card-token',
  standalone: true,
  imports: [
    DatePipe,
    MatProgressBar,
    ButtonPadraoComponent,
    SelectOneComponent,
    DatePickerPadraoComponent,
    FormFieldComponent
  ],
  templateUrl: './card-token.component.html',
  styleUrl: './card-token.component.css'
})
export class CardTokenComponent implements OnInit {

  private readonly msgService = inject(MsgService);
  private readonly webHookService = inject(WebhookService);
  private readonly usuarioService = inject(UsuarioService);
  private readonly destroyRef = inject(DestroyRef);

  webhook: WebhookModel = new WebhookModel();
  progressoVencimento?: number;

  usuarios: UsuarioResumo[] = [];
  usuarioSelecionado?: UsuarioResumo;

  readonly dataVencimento = signal<Moment | null>(null);
  readonly horaVencimento = signal<string>('');
  readonly gerando = signal<boolean>(false);
  readonly carregandoUsuarios = signal<boolean>(true);
  readonly carregandoVencimento = signal<boolean>(true);

  readonly dataMinima = moment();

  private usuarioIdPendente?: number;

  readonly motivoBloqueio = computed<string | null>(() => {
    if (!this.usuarioSelecionadoSig()) return 'Selecione um usuário para gerar o token.';
    const temData = !!this.dataVencimento();
    const temHora = !!this.horaVencimento().trim();
    if (temData !== temHora) return 'Informe a data e a hora de vencimento (ou deixe ambos em branco).';
    if (temData && temHora && !this.vencimentoNoFuturo()) {
      return 'A data e a hora de vencimento devem ser maiores que a data e a hora atuais.';
    }
    return null;
  });

  readonly usuarioSelecionadoSig = signal<UsuarioResumo | undefined>(undefined);

  ngOnInit() {
    this.buscarUsuariosAtivos();
    this.buscarWebhook();
  }

  buscarUsuariosAtivos() {
    this.usuarioService.buscarUsuarios().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        this.usuarios = (res ?? []).filter(u => u.ativo !== false);
        this.restaurarUsuarioSelecionado();
        this.carregandoUsuarios.set(false);
      },
      error: () => {
        this.carregandoUsuarios.set(false);
        this.msgService.msgErro('Erro ao buscar os usuários');
      }
    });
  }

  buscarWebhook() {
    this.webHookService.buscarWebhook().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        if (res) {
          this.webhook = res;
          this.usuarioIdPendente = res.usuarioId;
          this.restaurarUsuarioSelecionado();
        }
      },
      error: () => this.carregandoVencimento.set(false),
      complete: () => {
        this.calcularProgressoVencimento();
        this.carregandoVencimento.set(false);
      }
    });
  }

  private restaurarUsuarioSelecionado() {
    if (this.usuarioIdPendente == null || this.usuarios.length === 0) {
      return;
    }
    const encontrado = this.usuarios.find(u => u.id === this.usuarioIdPendente);
    if (encontrado) {
      this.usuarioSelecionado = encontrado;
      this.usuarioSelecionadoSig.set(encontrado);
    }
  }

  selecionarUsuario(usuario?: UsuarioResumo) {
    this.usuarioSelecionado = usuario;
    this.usuarioSelecionadoSig.set(usuario);
  }

  calcularProgressoVencimento() {
    if (this.webhook?.dataVencimento) {
      const dataInicio = new Date();
      const dataFinal = new Date(this.webhook.dataVencimento);
      const agora = new Date();
      const total = dataFinal.getTime() - dataInicio.getTime();
      const passado = agora.getTime() - dataInicio.getTime();
      this.progressoVencimento = Math.min(100, Math.max(0, (passado / total) * 100));
    }
  }

  private vencimentoNoFuturo(): boolean {
    const combinado = this.combinarDataHora();
    return combinado != null && combinado.isAfter(moment());
  }

  private combinarDataHora(): Moment | null {
    const data = this.dataVencimento();
    const hora = this.horaVencimento().trim();
    if (!data || !hora) {
      return null;
    }
    const [hh, mm] = hora.split(':');
    return data.clone().hour(Number(hh)).minute(Number(mm)).second(0).millisecond(0);
  }

  copiarToken() {
    navigator.clipboard.writeText(this.webhook?.tokenLocal ?? '')
      .then(() => this.msgService.msgSucesso('Token copiado'))
      .catch(() => this.msgService.msgAdvertencia('Não foi possível copiar'));
  }

  gerarNovoToken() {
    const motivo = this.motivoBloqueio();
    if (motivo) {
      this.msgService.msgAdvertencia(motivo);
      return;
    }
    if (this.gerando()) {
      return;
    }

    this.webhook.usuarioId = this.usuarioSelecionado!.id;
    const combinado = this.combinarDataHora();
    this.webhook.dataVencimento = combinado ? combinado.format('YYYY-MM-DDTHH:mm:00') : undefined;

    this.gerando.set(true);
    this.webHookService.gerarNovoToken(this.webhook).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.webhook.tokenLocal = res.token;
        if (res.dataExpiracao) {
          this.webhook.dataVencimento = new Date(
            res.dataExpiracao[0],
            res.dataExpiracao[1] - 1,
            res.dataExpiracao[2],
            res.dataExpiracao[3],
            res.dataExpiracao[4],
            res.dataExpiracao[5]
          );
        }
        this.msgService.msgSucesso('Novo token gerado com sucesso!');
        this.calcularProgressoVencimento();
      },
      error: (err) => this.msgService.msgErro(err?.error?.message ?? 'Ocorreu um erro ao gerar novo token'),
      complete: () => this.gerando.set(false)
    });
  }
}
